import React, { useState, useRef, forwardRef, useImperativeHandle, useContext } from 'react';
import { Drawer, Form, Space, Button, Input, Spin, Modal, Popconfirm, Table, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { CloseOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';
import { getTimeByUtcOffset } from '@/utils/intl';
import { DATE_TIME_FORMAT } from '@/config';
import * as ReverseApi from '@/api/bill/reverse';
import * as RefundApi from '@/api/bill/refund';
import { parse2Number, parseFen2Currency, parseFen2Yuan, parseYuan2Fen } from '@/utils/number';
import RefundByPaymentTypeModal, { IRefundRef } from '@/components/RefundByPaymentTypeModal';

import usePortalCalculator from '@/components/RefoundCalculatorModal';
import { isShowCalculator } from '@/components/RefoundCalculatorModal/help';
import IntlContext from '@/components/Intl/IntlContext';

import { validateAmount, formatAmount } from '../util/helper';
import { OpenPartRefundDrawerProps, PartRefundDrawerProps } from '../interface';
import { MT_ZL, XC_ZL, DY_ZL } from '../config';
import commonStyles from '../common.module.scss';
import PartRefundInfo from './info';
import styles from './index.module.scss';

const { TextArea } = Input;

const PartRefund: React.FC<PartRefundDrawerProps> = forwardRef((props, ref) => {
  const refundRef = useRef<IRefundRef>();

  const [partRefundForm] = Form.useForm();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [amount, setAmount] = useState(0); // 可退金额
  const [bill, setBill] = useState(null);
  const [partRefundAmount, setPartRefundAmount] = useState('');
  const [partRefundRemark, setPartRefundRemark] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const intlCtx = useContext(IntlContext);

  // 渠道退款计算器
  const calculatorPortal = usePortalCalculator();

  // 获取可退金额
  const fetchCanRefundAmount = async (no) => {
    try {
      const res = await ReverseApi.fetchReverseAmount({
        no,
      });
      if (res && res.status) {
        setAmount(res.data);
      }
    } catch (err) {
      setAmount(0);
    }
  };

  const openDrawer = (params: OpenPartRefundDrawerProps) => {
    setBill(params.bill);
    fetchCanRefundAmount(params.bill.no);
    setDrawerVisible(true);
  };

  useImperativeHandle(ref, () => ({
    openDrawer,
  }));

  // reset states after drawer closed
  const changeVisible = (visible) => {
    if (!visible) {
      setAmount(null);
      setBill(null);
      setPartRefundAmount('');
      setPartRefundRemark('');
    }
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
  };

  const columns: ColumnsType<any> = [
    {
      title: intl.get('bill_refound_res_pay_channel'),
      dataIndex: 'paymentType',
      width: 180,
      render: (v) => v || '-',
    },
    {
      title: intl.get('bill_refound_res_detail'),
      dataIndex: 'detail',
      render: (_, record) => {
        return `${record.paymentType}-${intl.get('bill_refound_res_channel_no')}${
          record.paymentNo
        }`;
      },
    },
    {
      title: intl.get('bill_refound_res_pay'),
      dataIndex: 'payAmount',
      width: 120,
      align: 'right',
      render: (val) => parseFen2Currency(val),
    },
    {
      title: intl.get('bill_refound_res_pay_time'),
      dataIndex: 'paymentTime',
      width: 200,
      render: (v) => getTimeByUtcOffset(v, DATE_TIME_FORMAT),
    },
    {
      title: intl.get('bill_refound_res_refound_amount'),
      dataIndex: 'refundAmount',
      width: 120,
      align: 'right',
      render: (val) => parseFen2Currency(val),
    },
    {
      title: intl.get('bill_refound_res_title'),
      dataIndex: 'status',
      width: 150,
      render: (v, r) => {
        if (v === 1) {
          return <span>{intl.get('bill_refound_res_success')}</span>;
        }
        if (v === 3) {
          return <span style={{ color: '#ff4d4f' }}>{r.reason}</span>;
        }

        return '';
      },
    },
  ];

  // 退款结果Modal
  const openResultModal = (result) => {
    const resultList = [result];

    Modal.info({
      title: (
        <span style={{ fontFamily: 'Alibaba-PuHuiTi-M' }}>
          {intl.get('bill_refound_res_title')}
        </span>
      ),
      width: 1200,
      icon: null,
      content: (
        <>
          <p style={{ margin: '24px 0' }}>{intl.get('bill_refound_res_refund_fail_tips')}</p>
          <Table rowKey="paymentNo" columns={columns} dataSource={resultList} pagination={false} />
        </>
      ),
      okText: intl.get('app_confirm_confirm_btn'),
      onOk: () => {
        closeDrawer();
        // callback parent component
        props.partRefundCallback();
      },
    });
  };

  // 金额输入框失焦
  const handleAmountInput = (value) => {
    const v = formatAmount(value, {
      max: parse2Number(parseFen2Yuan(amount)),
    });
    partRefundForm.setFieldsValue({
      partRefundAmount: v,
    });
  };

  // 确认退款
  const confirmRefund = async (data) => {
    const refundAmount = parse2Number(parseYuan2Fen(data.partRefundAmount)); // 退款金额

    try {
      setSubmitLoading(true);

      const params = {
        billItemNo: bill.no, // 退款账单号
        amount: refundAmount, // 退款金额
        remark: data.remark, // 备注
        resvNo: bill.resvNo,
      };
      const res = await RefundApi.confirmPartRefund(params);

      openResultModal(res?.data);
      setSubmitLoading(false);
    } catch (err) {
      // if (err?.code === BizErrorCode.REFUND_AMOUNT_ERROR) {
      //   message.error('退款失败，可退金额不足');
      // }
      Modal.error({
        title: intl.get('bill_cross_refound_fail'),
        content: (
          <div style={{ marginTop: 20 }}>
            <p>{intl.get('bill_cross_refound_fail_reason')}</p>
            <p>{err?.msg || ''}</p>
            <p style={{ marginTop: 20 }}>{intl.get('bill_cross_refound_retry')}</p>
          </div>
        ),
        closable: true,
        okText: intl.get('app_confirm_confirm_btn'),
      });
      setSubmitLoading(false);
    }
  };

  // 部分退款提交
  const submit = async () => {
    if (amount === 0) {
      message.warning(intl.get('bill_cross_refound_can_not_for_zero'));
      return;
    }

    let values;
    try {
      values = await partRefundForm.validateFields();
    } catch (err) {
      message.warning(intl.get('bill_cross_refound_input_refound_amount'));
    }

    // 退款二次确认
    refundRef.current?.showModal({
      dataSource: [
        {
          id: bill.no,
          subjectName: bill.subjectCodeName,
          paymentType: bill.paymentType,
          refundAmount: parse2Number(parseYuan2Fen(values?.partRefundAmount)),
        },
      ],
      onConfirm: async () => {
        await confirmRefund(values);
      },
    });
  };

  const drawerFooter = () => {
    // 美团直连、携程直连预付款退款二次确认
    const refundConfirm =
      bill?.paymentType === MT_ZL || bill?.paymentType === XC_ZL || bill?.paymentType === DY_ZL; // 直连预付款退款二次确认

    let channelName = '';
    if (bill?.paymentType === MT_ZL) {
      channelName = intl.get('app_meituan');
    }
    if (bill?.paymentType === XC_ZL) {
      channelName = intl.get('app_trip');
    }
    if (bill?.paymentType === DY_ZL) {
      channelName = intl.get('app_tiktok');
    }

    return (
      <Space size={16} style={{ marginLeft: 8 }}>
        {refundConfirm ? (
          <Popconfirm
            title={
              <div>
                <span>{intl.get('bill_cross_refound_confirm_refound_title', { channelName })}</span>
                <p style={{ color: 'red' }}>{intl.get('bill_cross_refound_alert')}</p>
              </div>
            }
            okText={intl.get('bill_cross_refound_confirm')}
            cancelText={intl.get('bill_cross_refound_click_wrong')}
            okButtonProps={{
              disabled: partRefundAmount === '' || +partRefundAmount <= 0,
              loading: submitLoading,
            }}
            onConfirm={() => submit()}
          >
            <Button
              type="primary"
              danger
              disabled={partRefundAmount === '' || +partRefundAmount <= 0}
            >
              {intl.get('bill_config_refound')}
            </Button>
          </Popconfirm>
        ) : (
          <Button
            type="primary"
            danger
            disabled={partRefundAmount === '' || +partRefundAmount <= 0}
            loading={submitLoading}
            onClick={() => submit()}
          >
            {intl.get('bill_config_refound')}
          </Button>
        )}
        <Button onClick={() => closeDrawer()}>{intl.get('app_confirm_cancel_btn')}</Button>
      </Space>
    );
  };

  // 计算退款金额
  const calculatorRefoundAmount = () => {
    const params = {
      channelNo: bill?.channelNo, // 渠道订单号
      payChanel: bill?.paymentType, // 支付渠道
      maxRefoundAmount: amount, // 最大可退金额
    };
    calculatorPortal.showDrawer({
      title: intl.get('bill_cross_refound_calculator'),
      cancelText: intl.get('app_confirm_close_btn'),
      ...params,
      onConfirm: async (refundAmount: any) => {
        partRefundForm.setFieldsValue({ partRefundAmount: refundAmount });
        setPartRefundAmount(refundAmount);
        calculatorPortal.closeDrawer();
      },
      onCancel: () => {
        calculatorPortal.closeDrawer();
      },
    });
  };

  return (
    <Drawer
      width="50%"
      title={intl.get('bill_part_refund_title')}
      open={drawerVisible}
      maskClosable={false}
      closable={false}
      destroyOnClose
      afterOpenChange={(visible) => changeVisible(visible)}
      extra={
        <Button
          style={{ border: 0 }}
          shape="circle"
          onClick={() => closeDrawer()}
          icon={<CloseOutlined />}
        />
      }
      footer={drawerFooter()}
    >
      <RefundByPaymentTypeModal ref={refundRef} />
      <PartRefundInfo info={bill} />
      <Spin spinning={submitLoading}>
        <dl className={styles['partRefund-amount']}>
          <dt className={commonStyles['bill-block-title']}>
            {intl.get('bill_cross_refound_infos')}
          </dt>
          <dd>
            <Form form={partRefundForm} preserve={false} className={styles['partRefund-form']}>
              <Form.Item label={intl.get('bill_cross_refound_avalible_amount')} name="amount">
                {parseFen2Currency(amount)}
              </Form.Item>
              <Form.Item label={intl.get('bill_cross_refound_amount')}>
                <Form.Item
                  name="partRefundAmount"
                  initialValue={partRefundAmount}
                  rules={[
                    {
                      validator: (_, value) => {
                        return validateAmount(value, {
                          required: true,
                          requiredMsg: intl.get('bill_cross_refound_input_refound_amount'),
                        });
                      },
                    },
                  ]}
                >
                  <Input
                    allowClear
                    prefix={intlCtx.TenantBaseConfig.CurrencySymbol}
                    placeholder={intl.get('bill_cross_refound_input_refound_amount')}
                    onChange={(e) => {
                      setPartRefundAmount(e.target.value);
                    }}
                    onBlur={(e) => handleAmountInput(e.target.value)}
                  />
                </Form.Item>
                {/* 退款渠道计算器入口 */}
                {isShowCalculator(bill?.subjectCode) && (
                  <span className={styles.refoundCalculator} onClick={calculatorRefoundAmount} />
                )}
              </Form.Item>
              <Form.Item
                label={intl.get('bill_remark')}
                name="remark"
                initialValue={partRefundRemark}
              >
                <TextArea
                  showCount
                  rows={4}
                  maxLength={10000}
                  placeholder={intl.get('bill_credit_account_remark_placeholder')}
                  onChange={(e) => {
                    setPartRefundRemark(e.target.value);
                  }}
                />
              </Form.Item>
            </Form>
          </dd>
        </dl>
      </Spin>
    </Drawer>
  );
});

export default PartRefund;
